import React from 'react';
import { Link } from 'react-router-dom';

export interface AdminSubscriptionRow {
  id: number;
  user_email: string | null;
  organization_name: string | null;
  type: string;
  name: string | null;
  is_active: boolean;
  created_at: string | null;
  last_notified_at: string | null;
  region: string | null;
  drug_id: number | null;
}

export interface AdminStarredListRow {
  id: number;
  user_email: string | null;
  organization_name: string | null;
  name: string | null;
  search_label: string | null;
  drug_count: number;
  drug_names: string[];
  drugs?: { drug_id: number | null; name: string; region: string | null }[];
  created_at: string | null;
}

export interface SavedItemsData {
  subscriptions: AdminSubscriptionRow[];
  starred_lists: AdminStarredListRow[];
}

export interface AdminAlertRow {
  id: number;
  user_email: string | null;
  organization_name: string | null;
  subscription_name: string | null;
  kind: string | null;
  display_name: string | null;
  version: string | null;
  status: string;
  created_at: string | null;
  sent_at: string | null;
}

export interface AdminResearchEmailRow {
  job_id: string;
  user_email: string | null;
  organization_name: string | null;
  recipient: string | null;
  query: string | null;
  sent_at: string | null;
}

export interface NotificationsData {
  alerts: AdminAlertRow[];
  research_emails: AdminResearchEmailRow[];
}

interface CommonProps {
  error: boolean;
  filterEmail: string;
  onFilterEmail: (email: string) => void;
  formatDate: (iso: string | null) => string;
}

const TH = 'text-left px-4 py-2 font-medium';

const PanelHeader: React.FC<{
  title: string;
  filterEmail: string;
  onFilterEmail: (email: string) => void;
}> = ({ title, filterEmail, onFilterEmail }) => (
  <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
    <h3 className="text-sm font-medium text-slate-700">
      {title}
      {filterEmail && <span className="text-indigo-600 ml-1">({filterEmail})</span>}
    </h3>
    {filterEmail && (
      <button onClick={() => onFilterEmail('')} className="text-xs text-slate-500 hover:underline">
        Clear filter
      </button>
    )}
  </div>
);

const UserCell: React.FC<{ email: string | null; onFilterEmail: (email: string) => void }> = ({
  email,
  onFilterEmail,
}) => (
  <td className="px-4 py-2 text-xs">
    {email ? (
      <button
        onClick={() => onFilterEmail(email.toLowerCase())}
        className="text-indigo-600 hover:underline"
      >
        {email}
      </button>
    ) : (
      '—'
    )}
  </td>
);

const TenantCell: React.FC<{ name: string | null }> = ({ name }) => (
  <td className="px-4 py-2 text-xs text-slate-600">{name || '—'}</td>
);

const SubSection: React.FC<{
  testId: string;
  label: string;
  count: number;
  headers: string[];
  children: React.ReactNode;
}> = ({ testId, label, count, headers, children }) => (
  <div data-testid={testId}>
    <h4 className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-50 border-b border-slate-200">
      {label} ({count})
    </h4>
    {count === 0 ? (
      <p className="px-4 py-3 text-sm text-slate-500">None.</p>
    ) : (
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            {headers.map((h) => (
              <th key={h} className={TH}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    )}
  </div>
);

const Panel: React.FC<{
  testId: string;
  title: string;
  errorText: string;
  error: boolean;
  filterEmail: string;
  onFilterEmail: (email: string) => void;
  children: React.ReactNode;
}> = ({ testId, title, errorText, error, filterEmail, onFilterEmail, children }) => (
  <div data-testid={testId} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
    <PanelHeader title={title} filterEmail={filterEmail} onFilterEmail={onFilterEmail} />
    <div className="max-h-[400px] overflow-auto">
      {error ? <p className="px-4 py-3 text-sm text-red-600">{errorText}</p> : children}
    </div>
  </div>
);

const DrugLink: React.FC<{ drugId: number | null; children: React.ReactNode }> = ({ drugId, children }) =>
  drugId != null ? (
    <Link to={`/drug/${drugId}`} className="text-indigo-600 hover:underline">
      {children}
    </Link>
  ) : (
    <>{children}</>
  );

export const SavedItemsPanel: React.FC<CommonProps & { data: SavedItemsData | null }> = ({
  data,
  error,
  filterEmail,
  onFilterEmail,
  formatDate,
}) => {
  const subs = data?.subscriptions ?? [];
  const searches = subs.filter((s) => s.type !== 'label');
  const labels = subs.filter((s) => s.type === 'label');
  const lists = data?.starred_lists ?? [];
  const subHeaders = ['User', 'Tenant', 'Name', 'Alerts', 'Created', 'Last alert'];

  const subRows = (rows: AdminSubscriptionRow[]) =>
    rows.map((s) => (
      <tr key={s.id} className="hover:bg-slate-50">
        <UserCell email={s.user_email} onFilterEmail={onFilterEmail} />
        <TenantCell name={s.organization_name} />
        <td className="px-4 py-2">
          <DrugLink drugId={s.drug_id}>{s.name || '—'}</DrugLink>
          {s.region && (
            <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
              {s.region}
            </span>
          )}
        </td>
        <td className="px-4 py-2">
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
              s.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
            }`}
          >
            {s.is_active ? 'On' : 'Off'}
          </span>
        </td>
        <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{formatDate(s.created_at)}</td>
        <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{formatDate(s.last_notified_at)}</td>
      </tr>
    ));

  return (
    <Panel
      testId="saved-items-panel"
      title="Saved Items"
      errorText="Couldn't load saved items."
      error={error}
      filterEmail={filterEmail}
      onFilterEmail={onFilterEmail}
    >
      <SubSection testId="saved-searches" label="Saved searches" count={searches.length} headers={subHeaders}>
        {subRows(searches)}
      </SubSection>
      <SubSection testId="watched-labels" label="Watched labels" count={labels.length} headers={subHeaders}>
        {subRows(labels)}
      </SubSection>
      <SubSection
        testId="starred-lists"
        label="Starred lists"
        count={lists.length}
        headers={['User', 'Tenant', 'Name', 'Search', 'Drugs', 'Created']}
      >
        {lists.map((l) => {
          const names = l.drug_names ?? [];
          const drugs = l.drugs ?? [];
          return (
            <tr key={l.id} className="hover:bg-slate-50">
              <UserCell email={l.user_email} onFilterEmail={onFilterEmail} />
              <TenantCell name={l.organization_name} />
              <td className="px-4 py-2">{l.name || '—'}</td>
              <td className="px-4 py-2 text-xs">{l.search_label || '—'}</td>
              <td className="px-4 py-2 text-xs max-w-md" title={names.join(', ')}>
                {drugs.length > 0 ? (
                  <>
                    {l.drug_count}:{' '}
                    {drugs.map((d, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && ', '}
                        <DrugLink drugId={d.drug_id}>{d.region ? `${d.name} (${d.region})` : d.name}</DrugLink>
                      </React.Fragment>
                    ))}
                  </>
                ) : names.length > 0 ? (
                  `${l.drug_count}: ${names.join(', ')}`
                ) : (
                  String(l.drug_count)
                )}
              </td>
              <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{formatDate(l.created_at)}</td>
            </tr>
          );
        })}
      </SubSection>
    </Panel>
  );
};

const ALERT_BADGES: Record<string, { cls: string; text: string }> = {
  sent: { cls: 'bg-green-100 text-green-700', text: 'sent' },
  failed: { cls: 'bg-red-100 text-red-700', text: 'failed' },
  pending: { cls: 'bg-amber-100 text-amber-700', text: 'pending' },
  in_app: { cls: 'bg-slate-100 text-slate-600', text: 'in-app only' },
};

const alertType = (kind: string | null) =>
  kind === 'label' ? 'Watched label' : kind === 'search' ? 'Saved search' : kind || '—';

export const NotificationsPanel: React.FC<CommonProps & { data: NotificationsData | null }> = ({
  data,
  error,
  filterEmail,
  onFilterEmail,
  formatDate,
}) => {
  const alerts = data?.alerts ?? [];
  const emails = data?.research_emails ?? [];

  return (
    <Panel
      testId="notifications-panel"
      title="Emails & Notifications"
      errorText="Couldn't load notifications."
      error={error}
      filterEmail={filterEmail}
      onFilterEmail={onFilterEmail}
    >
      <SubSection
        testId="alert-notifications"
        label="Alert notifications"
        count={alerts.length}
        headers={['Created', 'Sent', 'User', 'Tenant', 'Subscription', 'Type', 'Drug', 'Status']}
      >
        {alerts.map((a) => {
          const badge = ALERT_BADGES[a.status] ?? { cls: 'bg-slate-100 text-slate-700', text: a.status };
          return (
            <tr key={a.id} className="hover:bg-slate-50">
              <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{formatDate(a.created_at)}</td>
              <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{formatDate(a.sent_at)}</td>
              <UserCell email={a.user_email} onFilterEmail={onFilterEmail} />
              <TenantCell name={a.organization_name} />
              <td className="px-4 py-2">{a.subscription_name || '—'}</td>
              <td className="px-4 py-2 text-xs">{alertType(a.kind)}</td>
              <td className="px-4 py-2 text-xs">
                {a.display_name ? `${a.display_name}${a.version ? ` ${a.version}` : ''}` : '—'}
              </td>
              <td className="px-4 py-2">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
                  {badge.text}
                </span>
              </td>
            </tr>
          );
        })}
      </SubSection>
      <SubSection
        testId="research-emails"
        label="Research emails"
        count={emails.length}
        headers={['Sent', 'User', 'Tenant', 'Recipient', 'Query']}
      >
        {emails.map((r) => (
          <tr key={r.job_id} className="hover:bg-slate-50">
            <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{formatDate(r.sent_at)}</td>
            <UserCell email={r.user_email} onFilterEmail={onFilterEmail} />
            <TenantCell name={r.organization_name} />
            <td className="px-4 py-2 text-xs">{r.recipient || '—'}</td>
            <td className="px-4 py-2 text-xs truncate max-w-xs" title={r.query ?? ''}>
              {r.query || '—'}
            </td>
          </tr>
        ))}
      </SubSection>
    </Panel>
  );
};
